import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { X, Shield, Cookie } from "lucide-react";

const CONSENT_KEY = "cookie-consent-v2";
const CONSENT_EXPIRY_DAYS = 365;

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Check if consent exists in local storage
    const consent = localStorage.getItem(CONSENT_KEY);

    if (!consent) {
      // Delay showing the banner by a short time for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      // Check if consent has expired
      try {
        const consentData = JSON.parse(consent);
        const expiryDate = new Date(consentData.expiry);

        if (expiryDate < new Date()) {
          // Consent has expired, show banner again
          setShowBanner(true);
        }
      } catch (error) {
        // If there's an error parsing the consent data, show the banner
        setShowBanner(true);
      }
    }
  }, []);

  const handleAccept = (essentialOnly = false) => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + CONSENT_EXPIRY_DAYS);

    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({
        accepted: true,
        essentialOnly: essentialOnly,
        timestamp: new Date().toISOString(),
        expiry: expiry.toISOString(),
      })
    );

    setShowBanner(false);
  };

  const handleClose = () => {
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-lg ml-auto mr-4 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <Cookie className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold">Cookie Preferences</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              We use cookies to enhance your browsing experience, analyze site
              traffic, and personalize content. By clicking "Accept All", you
              consent to our use of cookies as described in our{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>

            {expanded && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-start space-x-3 pt-3">
                  <div className="mt-0.5">
                    <Shield className="h-4 w-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Essential Cookies</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      These cookies are necessary for the website to function
                      properly and cannot be disabled.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-0.5">
                    <Cookie className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Analytics Cookies</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Help us understand how visitors interact with our website
                      by collecting and reporting information anonymously.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-0.5">
                    <Cookie className="h-4 w-4 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Marketing Cookies</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Allow us to provide you with relevant content and
                      marketing through third-party tracking and advertising.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleAccept(false)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                Accept All
              </button>
              <button
                onClick={() => handleAccept(true)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
              >
                Essential Only
              </button>
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full sm:w-auto px-4 py-2 text-primary text-sm font-medium hover:underline"
              >
                {expanded ? "Show Less" : "Customize Settings"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
